/**
 * Reusable input-masking helpers (vanilla TS, no framework).
 *
 * Design goals:
 *  - DISPLAY a formatted value while the underlying normalized value stays as
 *    raw digits (read via `getRaw()` for submission).
 *  - NEVER exhibit the "types backwards" / caret-jump symptom. Masking works by
 *    reassigning `input.value` on each `input` event, which naively resets the
 *    caret to the end. We fix this by counting how many DIGITS precede the caret
 *    before reformatting, then placing the caret right after that same digit in
 *    the newly formatted string. This makes forward typing, mid-string edits,
 *    backspace/delete, and paste all behave normally.
 *
 * The pure `computeMasked` function contains all the logic and is unit-tested
 * without a DOM; `attachMask` is the thin DOM wrapper.
 */

export type Formatter = (rawDigits: string) => string;

/** Strip everything except digits. */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/** US phone: (XXX) XXX-XXXX, progressive as digits arrive (max 10). */
export function formatPhone(raw: string): string {
  const d = onlyDigits(raw).slice(0, 10);
  const area = d.slice(0, 3);
  const prefix = d.slice(3, 6);
  const line = d.slice(6, 10);
  if (d.length > 6) return `(${area}) ${prefix}-${line}`;
  if (d.length > 3) return `(${area}) ${prefix}`;
  if (d.length > 0) return `(${area}`;
  return "";
}

/** SSN: XXX-XX-XXXX (max 9 digits). */
export function formatSsn(raw: string): string {
  const d = onlyDigits(raw).slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/** EIN: XX-XXXXXXX (max 9 digits). */
export function formatEin(raw: string): string {
  const d = onlyDigits(raw).slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
}

/** ZIP: XXXXX or XXXXX-XXXX (max 9 digits). */
export function formatZip(raw: string): string {
  const d = onlyDigits(raw).slice(0, 9);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Index in `formatted` positioned right AFTER the `n`-th digit.
 * n <= 0 -> start of string; n greater than the digit count -> end of string.
 */
export function caretAfterNthDigit(formatted: string, n: number): number {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted.charCodeAt(i) >= 48 && formatted.charCodeAt(i) <= 57) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return formatted.length;
}

export interface MaskResult {
  /** Formatted string to display. */
  value: string;
  /** Caret index to restore (after the same logical digit the user was at). */
  caret: number;
  /** Normalized raw digits (what should be submitted). */
  raw: string;
}

/**
 * Pure masking step. Given the current raw input value and caret position,
 * returns the formatted value, the caret position to restore, and the raw digits.
 */
export function computeMasked(
  oldValue: string,
  selStart: number,
  format: Formatter,
  maxDigits: number,
): MaskResult {
  const digitsBeforeCaret = onlyDigits(oldValue.slice(0, selStart)).length;
  const raw = onlyDigits(oldValue).slice(0, maxDigits);
  const value = format(raw);
  const cappedDigitsBeforeCaret = Math.min(digitsBeforeCaret, raw.length);
  const caret = caretAfterNthDigit(value, cappedDigitsBeforeCaret);
  return { value, caret, raw };
}

export interface MaskController {
  /** Normalized value (digits only) for submission. */
  getRaw(): string;
  /** Re-run formatting on the current value without moving the caret logically. */
  reformat(): void;
  /** Swap the formatter (e.g. SSN <-> EIN) and reformat in place. */
  setFormatter(format: Formatter, maxDigits: number): void;
}

/**
 * Attach a live mask to a text/tel input. Returns a controller whose `getRaw()`
 * yields the normalized digits to submit.
 */
export function attachMask(
  input: HTMLInputElement,
  format: Formatter,
  maxDigits: number,
): MaskController {
  let currentFormat = format;
  let currentMax = maxDigits;

  const apply = (preserveCaret: boolean): void => {
    const selStart = input.selectionStart ?? input.value.length;
    const result = computeMasked(input.value, selStart, currentFormat, currentMax);
    input.value = result.value;
    input.dataset.raw = result.raw;
    // Only touch the caret while the field is focused, so programmatic reformats
    // (e.g. switching SSN/EIN) don't steal focus or scroll.
    if (preserveCaret && document.activeElement === input) {
      input.setSelectionRange(result.caret, result.caret);
    }
  };

  input.addEventListener("input", () => apply(true));
  input.dataset.raw = onlyDigits(input.value).slice(0, currentMax);

  return {
    getRaw: () => onlyDigits(input.value).slice(0, currentMax),
    reformat: () => apply(false),
    setFormatter: (nextFormat: Formatter, nextMax: number) => {
      currentFormat = nextFormat;
      currentMax = nextMax;
      apply(false);
    },
  };
}
